/** NOTIF1 T17: client-side search for the Ctrl/⌘K palette and the Élèves/Familles filters (N6). */
import { SEARCH_COPY as COPY } from '../../constants/search_messages.fr';

export const ENTITY_LIMIT = 5;
export const RECENTS_MAX = 5;
const RECENTS_KEY = 'np_palette_recents';
const DIACRITICS = /[̀-ͯ]/g;

export const RESULT_KIND = Object.freeze({ STUDENT: 'student', FAMILY: 'family', PAGE: 'page', ACTION: 'action' });

/** Lower case, no accents: "Élève" → "eleve". */
export const normalize = (value) => String(value ?? '').normalize('NFD').replace(DIACRITICS, '').toLowerCase();

const MIN_TOKEN_LENGTH = 2;
const WORD_SPLIT = /[^a-z0-9]+/;
/** Query words worth matching against names ("un", "a" dropped). */
const tokensOf = (query) => normalize(query).split(/\s+/).filter((token) => token.length >= MIN_TOKEN_LENGTH);
const wordsOf = (text) => normalize(text).split(WORD_SPLIT).filter(Boolean);

/**
 * Score = query tokens that START a word of the text (+1 when the whole word matches).
 * Command words of a sentence ("planifier un cours avec sara") simply score 0, the name scores.
 */
const scoreOf = (text, tokens) => {
  const words = wordsOf(text);
  return tokens.reduce((score, token) => {
    if (words.includes(token)) return score + 2;
    return words.some((word) => word.startsWith(token)) ? score + 1 : score;
  }, 0);
};

/** Items with score > 0, best first (stable for ties). */
const rank = (items, textOf, tokens) => items
  .map((item, index) => ({ item, index, score: scoreOf(textOf(item), tokens) }))
  .filter((row) => row.score > 0)
  .sort((a, b) => b.score - a.score || a.index - b.index)
  .map((row) => row.item);

/**
 * Splits `text` into [{text, hit}] where hits are the query tokens, matched without accents.
 * Normalising is done per character so indexes map back to the original string.
 */
export function highlightParts(text, query) {
  const source = String(text ?? '');
  const tokens = tokensOf(query);
  if (!source || !tokens.length) return [{ text: source, hit: false }];
  const chars = [...source];
  const folded = chars.map((char) => (normalize(char).charAt(0) || char)).join('');
  const hits = new Array(chars.length).fill(false);
  const wordStart = (index) => index === 0 || WORD_SPLIT.test(folded[index - 1]);
  tokens.forEach((token) => {
    let from = folded.indexOf(token);
    while (from !== -1) {
      if (wordStart(from)) for (let i = from; i < from + token.length; i += 1) hits[i] = true;
      from = folded.indexOf(token, from + 1);
    }
  });
  return chars.reduce((parts, char, index) => {
    const last = parts[parts.length - 1];
    if (last && last.hit === hits[index]) last.text += char;
    else parts.push({ text: char, hit: hits[index] });
    return parts;
  }, []);
}

const fullName = (row) => `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim();
const familyText = (family) => normalize([family.first_name, family.last_name, family.new_last_name,
  family.email, family.city_label].filter(Boolean).join(' '));

/**
 * Students and families ranked by name hits; a family also matches through its children's names,
 * a student through its family's name ("Léa" finds famille Fictif; "Fictif" finds Léa).
 * Works inside a sentence: "planifier un cours avec sara" → Sara + her family.
 */
export function matchEntities(query, students = [], families = [], limit = ENTITY_LIMIT) {
  const tokens = tokensOf(query);
  if (!tokens.length) return { students: [], families: [] };
  const childrenByFamily = new Map();
  students.forEach((student) => {
    const list = childrenByFamily.get(student.family_id) || [];
    list.push(normalize(fullName(student)));
    childrenByFamily.set(student.family_id, list);
  });
  const familyById = new Map(families.map((family) => [family.family_id, family]));

  const matchedFamilies = rank(families,
    (family) => `${familyText(family)} ${(childrenByFamily.get(family.family_id) || []).join(' ')}`, tokens);
  const matchedStudents = rank(students, (student) => {
    const family = familyById.get(student.family_id);
    return `${fullName(student)} ${student.family_name ?? ''} ${family ? familyText(family) : ''}`;
  }, tokens);
  return { students: matchedStudents.slice(0, limit), families: matchedFamilies.slice(0, limit) };
}

/** App pages reachable from the palette (`view` = App setCurrentView key). */
export const PAGES = Object.freeze([
  { id: 'home', view: 'home', label: COPY.pages.home },
  { id: 'students', view: 'students', label: COPY.pages.students },
  { id: 'families', view: 'families', label: COPY.pages.families },
  { id: 'payments', view: 'payments', label: COPY.pages.payments },
  { id: 'invoices', view: 'generateInvoice', label: COPY.pages.invoices },
  { id: 'statistics', view: 'statistics', label: COPY.pages.statistics },
  { id: 'schedules', view: 'schedules', label: COPY.pages.schedules },
  { id: 'notifications', view: 'notifications', label: COPY.pages.notifications },
  { id: 'settings', view: 'settings', label: COPY.pages.settings },
  { id: 'tools', view: 'tools', label: COPY.pages.tools },
]);

export const matchPages = (query) => {
  const tokens = tokensOf(query);
  return tokens.length ? rank(PAGES, (page) => page.label, tokens) : [];
};

/** Empty-state "Actions rapides": proposals run by runProposal. */
export const QUICK_ACTIONS = Object.freeze([
  { id: 'quick-start', icon: 'Play', label: COPY.quick.startCourse, proposal: { intent: 'start_course', payload: {} } },
  { id: 'quick-student', icon: 'GraduationCap', label: COPY.quick.addStudent, proposal: { intent: 'add_student', payload: {} } },
  { id: 'quick-payment', icon: 'CreditCard', label: COPY.quick.recordPayment, proposal: { intent: 'record_payment', payload: {} } },
  { id: 'quick-urssaf', icon: 'Shield', label: COPY.quick.declareUrssaf, proposal: { intent: 'urssaf', payload: {} } },
]);

/** Recents keep ids only (no names in storage); labels come from the loaded lists. */
export function loadRecents() {
  try {
    const value = JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]');
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

export function pushRecent(entry) {
  const next = [entry, ...loadRecents().filter((item) => !(item.kind === entry.kind && item.id === entry.id))]
    .slice(0, RECENTS_MAX);
  try { localStorage.setItem(RECENTS_KEY, JSON.stringify(next)); } catch { /* private mode */ }
  return next;
}

export const isApplePlatform = (nav = typeof navigator === 'undefined' ? null : navigator) => (
  /mac|iphone|ipad|ipod/i.test(nav?.userAgentData?.platform || nav?.platform || '')
);

export const shortcutHint = (apple = isApplePlatform()) => (apple ? COPY.kbdApple : COPY.kbdOther);

const INVOICE_INTENTS = new Set(['generate_invoice', 'send_invoice', 'attestation_sap']);
const ENTITY_INTENTS = new Set(['view_student', 'view_family']);

const studentItem = (student) => ({
  key: `student-${student.student_id}`, kind: RESULT_KIND.STUDENT, icon: 'GraduationCap',
  label: fullName(student), sub: student.family_name ? COPY.familyOf(student.family_name) : '',
  recent: { kind: RESULT_KIND.STUDENT, id: student.student_id },
  proposal: { intent: 'view_student', payload: { student } },
});

const familyItem = (family, students) => ({
  key: `family-${family.family_id}`, kind: RESULT_KIND.FAMILY, icon: 'Users',
  label: COPY.familyOf(family.last_name || fullName(family)),
  sub: students.filter((s) => s.family_id === family.family_id).map((s) => s.first_name).join(', '),
  recent: { kind: RESULT_KIND.FAMILY, id: family.family_id },
  proposal: { intent: 'view_family', payload: { family } },
});

const pageItem = (page) => ({
  key: `page-${page.id}`, kind: RESULT_KIND.PAGE, icon: 'ChevronRight', label: page.label,
  recent: { kind: RESULT_KIND.PAGE, id: page.id },
  proposal: { intent: 'open_page', payload: { view: page.view } },
});

const proposalItem = (proposal) => ({
  key: `proposal-${proposal.id}`, kind: RESULT_KIND.ACTION, icon: proposal.icon,
  label: proposal.label, sub: proposal.desc, proposal,
});

function recentItem(entry, students, families) {
  if (entry.kind === RESULT_KIND.STUDENT) {
    const student = students.find((s) => s.student_id === entry.id);
    return student ? studentItem(student) : null;
  }
  if (entry.kind === RESULT_KIND.FAMILY) {
    const family = families.find((f) => f.family_id === entry.id);
    return family ? familyItem(family, students) : null;
  }
  const page = PAGES.find((p) => p.id === entry.id);
  return page ? pageItem(page) : null;
}

/**
 * Palette sections. Empty query: Actions rapides + Récents.
 * Typed: Propositions (French commands) · Élèves · Familles · Factures · Pages. Empty groups dropped.
 */
export function paletteGroups({ query, proposals = [], students = [], families = [], recents = [] }) {
  if (!normalize(query).trim()) {
    return [
      { id: 'quick', label: COPY.groups.quick, items: QUICK_ACTIONS.map((action) => ({ ...action, key: action.id, kind: RESULT_KIND.ACTION })) },
      { id: 'recents', label: COPY.groups.recents, items: recents.map((entry) => recentItem(entry, students, families)).filter(Boolean) },
    ].filter((group) => group.items.length);
  }
  const entities = matchEntities(query, students, families);
  const shownStudents = new Set(entities.students.map((s) => s.student_id));
  const shownFamilies = new Set(entities.families.map((f) => f.family_id));
  // A "Fiche de …" proposal is dropped only when the same entity is already in Élèves / Familles.
  const alreadyShown = (p) => ENTITY_INTENTS.has(p.intent)
    && (shownStudents.has(p.payload?.student?.student_id) || shownFamilies.has(p.payload?.family?.family_id));
  return [
    { id: 'proposals', label: COPY.groups.proposals,
      items: proposals.filter((p) => !INVOICE_INTENTS.has(p.intent) && !alreadyShown(p)).map(proposalItem) },
    { id: 'students', label: COPY.groups.students, items: entities.students.map(studentItem) },
    { id: 'families', label: COPY.groups.families, items: entities.families.map((f) => familyItem(f, students)) },
    { id: 'invoices', label: COPY.groups.invoices, items: proposals.filter((p) => INVOICE_INTENTS.has(p.intent)).map(proposalItem) },
    { id: 'pages', label: COPY.groups.pages, items: matchPages(query).map(pageItem) },
  ].filter((group) => group.items.length);
}
